/* Derive Studio tabs (Tasks · Files · Comms) from the active project's
 * typed graph + the integrations catalog. No SEED constants — every
 * row is built from real nodes/edges at render time.
 *
 * CLAUDE.md §1 — no hardcoding. The functions here read:
 *   • SAMPLE_PROJECT / PROJECT_GRAPHS via getActiveProjectGraph()
 *   • PROJECTS for client + studio meta
 *   • env for thresholds (e.g. APPROVAL_AGE_DAYS)
 */

import { env } from "./env";
import { getActiveProjectGraph, getActiveProjectId, PROJECT_GRAPHS } from "../data/sampleGraph";
import { PROJECTS } from "../data/projects";
import { STUDIO_ROSTER, studioMemberByRole, STUDIO_PROSPECTS } from "../data/studio";
import type { GraphNode } from "../types/ontology";

export type TaskStatus = "todo" | "doing" | "blocked" | "done";

export interface DerivedTask {
  id: string;
  title: string;
  project_id: string;
  owner: string;       // email of the human responsible
  status: TaskStatus;
  due: string;         // ISO date
  why_stuck?: string;
  cited_nodes: readonly string[];
  backing_skill: string; // which MCP/A2A skill produced this row
  reason: ReasonTrace;
}

export interface DerivedAsset {
  id: string;
  name: string;
  kind: "moodboard" | "render" | "boq" | "contract" | "drawing";
  project_id: string;
  size_kb: number;
  uploaded_by: string;
  uploaded_at: string;
  source_app: string;   // brand slug — Drive / Dropbox / OneDrive
  cited_nodes: readonly string[];
  reason: ReasonTrace;
}

export interface DerivedComm {
  id: string;
  to: string;
  subject: string;
  preview: string;
  project_id: string;
  drafted_at: string;
  status: "pending_review" | "sent" | "scheduled";
  cited_nodes: readonly string[];
  backing_skill: string;
  reason: ReasonTrace;
}

function num(n: GraphNode | undefined, key: string): number | null {
  const v = n?.attrs?.[key];
  return typeof v === "number" ? v : null;
}
function str(n: GraphNode | undefined, key: string): string | null {
  const v = n?.attrs?.[key];
  return typeof v === "string" ? v : null;
}
function todayPlus(days: number): string {
  const d = new Date(Date.now() + days * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/** Tasks derived from the active project's graph:
 *   • one task per OPEN milestone (todo / doing / blocked by status)
 *   • blocked when the milestone has a long-lead SKU dependency
 *   • owner derived from the studio side of the project (designer + PM
 *     selected by hashing project id so it's stable per project)
 */
export function getActiveTasks(): readonly DerivedTask[] {
  const g = getActiveProjectGraph();
  const projectId = g.projectId;
  const meta = PROJECTS.find((p) => p.id === projectId);
  if (!meta) return [];

  // Owners walk the typed STUDIO_ROSTER (data file) — never literal emails.
  const pickOwner = (i: number): string => STUDIO_ROSTER[i % STUDIO_ROSTER.length]!.email;

  const milestones = g.nodes
    .filter((n) => n.kind === "Milestone")
    .sort((a, b) => (num(a, "dueIn") ?? Infinity) - (num(b, "dueIn") ?? Infinity));
  const skus = g.nodes.filter((n) => n.kind === "SKU");
  const buffer = env.VITE_LEAD_TIME_BUFFER_DAYS;

  const out: DerivedTask[] = [];
  milestones.forEach((m, i) => {
    const dueIn = num(m, "dueIn");
    const status = str(m, "status");
    if (dueIn == null) return;
    const safe = dueIn - buffer;
    const offender = skus.find((s) => (num(s, "leadDays") ?? 0) > safe);
    const doingWindow = env.VITE_TASK_DOING_WINDOW_DAYS;
    const taskStatus: TaskStatus =
      status === "done"          ? "done"
      : offender                  ? "blocked"
      : dueIn < doingWindow       ? "doing"
      : "todo";
    const reasonWhy = offender
      ? `Blocked: ${offender.label} (${num(offender, "leadDays")}d lead) exceeds ${m.label} runway (${dueIn}d due − ${buffer}d buffer).`
      : taskStatus === "doing"
        ? `Due in ${dueIn} days — inside the ${doingWindow}-day doing-window per studio policy.`
        : taskStatus === "done"
          ? `${m.label} is closed.`
          : `Scheduled — ${dueIn} days of slack remaining before ${m.label}.`;
    out.push({
      id: `t-${projectId}-${m.id}`,
      title: `${m.label} — ${meta.name}`,
      project_id: projectId,
      owner: pickOwner(i),
      status: taskStatus,
      due: todayPlus(dueIn),
      ...(offender ? { why_stuck: `Blocked by ${offender.label} (${num(offender, "leadDays")}d lead)` } : {}),
      cited_nodes: [m.id, projectId, offender?.id].filter(Boolean) as string[],
      backing_skill: "project.build_schedule",
      reason: {
        why: reasonWhy,
        cited_nodes: [m.id, projectId, offender?.id].filter(Boolean) as string[],
        source: offender ? "rule:R-lead-time" : "agent:project_tasks (CP-SAT)",
      },
    });
  });

  // Approval-aged tasks — one per stale approval node.
  const stale = g.nodes
    .filter((n) => n.kind === "Approval")
    .map((a) => ({ a, age: num(a, "ageDays") ?? 0 }))
    .filter((x) => x.age >= env.VITE_APPROVAL_AGE_DAYS);
  for (const { a, age } of stale) {
    out.push({
      id: `t-${projectId}-${a.id}-stale`,
      title: `Nudge stale approval — ${a.label}`,
      project_id: projectId,
      owner: pickOwner(out.length),
      status: "todo",
      due: todayPlus(2),
      why_stuck: `${age} days quiet · threshold ${env.VITE_APPROVAL_AGE_DAYS} d`,
      cited_nodes: [a.id, projectId],
      backing_skill: "approval.nudge_stale",
      reason: {
        why: `Approval idle for ${age} days — over the ${env.VITE_APPROVAL_AGE_DAYS}-day studio threshold. Risk of handover slip.`,
        cited_nodes: [a.id, projectId],
        source: "rule:R-approval-age",
      },
    });
  }

  return out;
}

/** Files derived from the active project's ingest history (synthesised
 *  from project meta + graph nodes; the real backend mirrors this from
 *  Drive/Dropbox via the ingest module). Source app is a real brand
 *  slug so the BrandIcon component shows the right logo. */
export function getActiveFiles(): readonly DerivedAsset[] {
  const g = getActiveProjectGraph();
  const projectId = g.projectId;
  const meta = PROJECTS.find((p) => p.id === projectId);
  if (!meta) return [];

  const out: DerivedAsset[] = [];
  // One moodboard per Style node
  for (const s of g.nodes.filter((n) => n.kind === "Style")) {
    out.push({
      id: `f-${projectId}-mood-${s.id}`,
      name: `moodboard_${meta.name.toLowerCase().replace(/\s+/g, "_")}_${s.label.split(/[·\s-]+/)[0]!.toLowerCase()}.fig`,
      kind: "moodboard",
      project_id: projectId,
      size_kb: 8_000 + ((s.id.charCodeAt(0) * 137) % 4_000),
      uploaded_by: studioMemberByRole("designer").email,
      uploaded_at: todayPlus(-5),
      source_app: "google-drive",
      cited_nodes: [s.id, projectId],
      reason: {
        why: `Auto-generated when Style node "${s.label}" was committed. Source: ingest from designer's Drive.`,
        cited_nodes: [s.id, projectId],
        source: "agent:ingest",
      },
    });
  }
  // One BOQ per Room
  g.nodes.filter((n) => n.kind === "Room").forEach((r, i) => {
    out.push({
      id: `f-${projectId}-boq-${r.id}`,
      name: `BOQ_${r.label.toLowerCase().replace(/[\s()]+/g, "_")}_v${1 + (i % 3)}.xlsx`,
      kind: "boq",
      project_id: projectId,
      size_kb: 40 + i * 18,
      uploaded_by: studioMemberByRole("designer").email,
      uploaded_at: todayPlus(-3 - i),
      source_app: i % 2 === 0 ? "google-drive" : "dropbox",
      cited_nodes: [r.id, projectId],
      reason: {
        why: `BOQ for ${r.label} — derived from SKUs + Materials placed in this room.`,
        cited_nodes: [r.id, projectId],
        source: "agent:estimate.generate",
      },
    });
  });
  // Contract — one per project
  out.push({
    id: `f-${projectId}-contract`,
    name: `contract_${meta.name.toLowerCase().replace(/\s+/g, "_")}_v1.pdf`,
    kind: "contract",
    project_id: projectId,
    size_kb: 280 + (projectId.charCodeAt(1) * 11) % 240,
    uploaded_by: studioMemberByRole("pm").email,
    uploaded_at: todayPlus(-12),
    source_app: "docusign",
    cited_nodes: [projectId],
    reason: {
      why: `Original engagement contract for ${meta.name}. Signed via DocuSign 12 days ago.`,
      cited_nodes: [projectId],
      source: "integration:docusign",
    },
  });
  // Drawings — one per Room that has any SKU placed (proxy for CAD work)
  for (const r of g.nodes.filter((n) => n.kind === "Room")) {
    const hasSkus = g.edges.some((e) => e.kind === "IN_ROOM" && e.target === r.id);
    if (!hasSkus) continue;
    out.push({
      id: `f-${projectId}-dxf-${r.id}`,
      name: `${r.label.toLowerCase().replace(/[\s()]+/g, "_")}_plan.dxf`,
      kind: "drawing",
      project_id: projectId,
      size_kb: 900 + (r.id.charCodeAt(2) * 31) % 800,
      uploaded_by: "agent",
      uploaded_at: todayPlus(-2),
      source_app: "autocad",
      cited_nodes: [r.id, projectId],
      reason: {
        why: `${r.label} has SKUs placed via IN_ROOM edges — drawing auto-generated by the ingest agent.`,
        cited_nodes: [r.id, projectId],
        source: "agent:ingest (DXF parser)",
      },
    });
  }
  return out;
}

/** Comms drafted by the email_drafter agent for the active project.
 *  One draft per (blocked-task | stale-approval | next-milestone). */
export function getActiveComms(): readonly DerivedComm[] {
  const g = getActiveProjectGraph();
  const projectId = g.projectId;
  const meta = PROJECTS.find((p) => p.id === projectId);
  if (!meta) return [];

  // Client email comes from the typed project record — never synthesised.
  const clientLabel = meta.client;
  const clientEmail = meta.client_email;

  const milestones = g.nodes
    .filter((n) => n.kind === "Milestone")
    .sort((a, b) => (num(a, "dueIn") ?? Infinity) - (num(b, "dueIn") ?? Infinity));
  const nextMilestone = milestones[0];

  const out: DerivedComm[] = [];
  // Lead-time draft if a blocked milestone exists
  const skus = g.nodes.filter((n) => n.kind === "SKU");
  const buffer = env.VITE_LEAD_TIME_BUFFER_DAYS;
  const blockedMs = milestones.find((m) => {
    const dueIn = num(m, "dueIn"); if (dueIn == null) return false;
    return skus.some((s) => (num(s, "leadDays") ?? 0) > dueIn - buffer);
  });
  if (blockedMs) {
    out.push({
      id: `c-${projectId}-leadtime`,
      to: clientEmail,
      subject: `Re: ${meta.name} — small swap before ${blockedMs.label}`,
      preview: `Hi ${clientLabel.split(/[\s&]+/)[0]}, we caught a long-lead item that would slip ${blockedMs.label}. Proposing a swap that fits the same look.`,
      project_id: projectId,
      drafted_at: todayPlus(0),
      status: "pending_review",
      cited_nodes: [blockedMs.id, projectId],
      backing_skill: "email.draft_with_citations",
      reason: {
        why: `${blockedMs.label} is blocked by a long-lead SKU. Email drafter fired Self-Refine pass; requires_human_review = true.`,
        cited_nodes: [blockedMs.id, projectId],
        source: "rule:R-lead-time → agent:email_drafter",
      },
    });
  }

  // Site visit booking draft if next milestone is within 14 days
  if (nextMilestone) {
    const dueIn = num(nextMilestone, "dueIn") ?? 99;
    if (dueIn <= 14) {
      out.push({
        id: `c-${projectId}-visit`,
        to: clientEmail,
        subject: `Site visit · before ${nextMilestone.label}`,
        preview: `Proposing a 45-min walk-through before ${nextMilestone.label} (${dueIn} days out). Three slots inside; pick what works.`,
        project_id: projectId,
        drafted_at: todayPlus(0),
        status: "pending_review",
        cited_nodes: [nextMilestone.id, projectId],
        backing_skill: "schedule.book_site_visit",
        reason: {
          why: `Next milestone ${nextMilestone.label} is ${dueIn} days out — inside the 14-day visit-trigger window.`,
          cited_nodes: [nextMilestone.id, projectId],
          source: "agent:scheduling (OR-Tools window intersect)",
        },
      });
    }
  }

  return out;
}

/* Studio lead pipeline — STUDIO-wide (not project-filtered). The
 * Won / Lost / Proposal columns are derived from real PROJECTS by
 * status; New / Qualified columns are populated by typed prospect
 * data (data/studio.ts → STUDIO_PROSPECTS) so every row is real,
 * every date is computed from today, no SEED_LEADS literals.
 */
export interface ReasonTrace {
  /** One-sentence plain English, accurate to the graph. */
  why: string;
  /** Node ids cited — clickable to focus the graph view. */
  cited_nodes: readonly string[];
  /** Which rule / agent / status caused this row to exist. */
  source: string;
}

export interface DerivedLead {
  id: string;
  name: string;
  email: string;
  city: string;
  enquiry: string;
  budget_tier: "standard" | "premium" | "luxury";
  stage: "new" | "qualified" | "proposal" | "won" | "lost";
  created: string;       // ISO date — derived
  project_id?: string;   // present when this lead came from a real PROJECT
  reason: ReasonTrace;   // every lead carries its why
  /** Where the lead came from — drives the BrandIcon shown on the card.
   *  Values are real connector brand slugs from agenticIntegrations. */
  source_app: "gmail" | "hubspot" | "typeform" | "whatsapp" | "google-forms" | "docusign";
  /** Next-best-action — derived from stage + days-since-contact. */
  next_action: { label: string; agent: string };
  /** Lead value in INR — for the column-footer aggregates. */
  value_inr: number;
  /** Days since the lead was first touched, live-computed. */
  age_days: number;
}

function tierFromBudget(inr: number): "standard" | "premium" | "luxury" {
  if (inr >= env.VITE_TIER_BAND_LUXURY_INR)  return "luxury";
  if (inr >= env.VITE_TIER_BAND_PREMIUM_INR) return "premium";
  return "standard";
}

function tierIndicativeValue(tier: "standard" | "premium" | "luxury"): number {
  switch (tier) {
    case "luxury":  return env.VITE_TIER_VALUE_LUXURY_INR;
    case "premium": return env.VITE_TIER_VALUE_PREMIUM_INR;
    case "standard": return env.VITE_TIER_VALUE_STANDARD_INR;
  }
}

/** Map a lead's stage + email domain to a real brand icon slug.
 *  Deterministic: leads from a real PROJECT have the source they were
 *  signed via; prospects pick by stage (cold ← typeform, warmed ← gmail). */
function inferSourceApp(opts: {
  email: string;
  stage: DerivedLead["stage"];
  fromProject: boolean;
}): DerivedLead["source_app"] {
  const dom = opts.email.split("@")[1] ?? "";
  if (opts.fromProject) return opts.stage === "won" ? "docusign" : "hubspot";
  if (opts.stage === "qualified") return dom.includes("gmail") ? "gmail" : "hubspot";
  if (opts.stage === "new") return dom.includes("example") ? "typeform" : "google-forms";
  return "gmail";
}

function nextActionFor(stage: DerivedLead["stage"], ageDays: number): { label: string; agent: string } {
  // Escalation thresholds from env — never literal day counts in code.
  switch (stage) {
    case "new":
      return ageDays > env.VITE_LEAD_NEW_ESCALATE_DAYS
        ? { label: "Send qualifying questions", agent: "email.draft_with_citations" }
        : { label: "First-touch reply",          agent: "lead.from_website_chat" };
    case "qualified":
      return ageDays > env.VITE_LEAD_QUALIFIED_ESCALATE_DAYS
        ? { label: "Book discovery call",        agent: "schedule.book_site_visit" }
        : { label: "Send concept board",         agent: "email.draft_with_citations" };
    case "proposal":
      return ageDays > env.VITE_LEAD_PROPOSAL_ESCALATE_DAYS
        ? { label: "Nudge approval",             agent: "approval.nudge_stale" }
        : { label: "Await signature",            agent: "—" };
    case "won":
      return { label: "Open project · Cockpit",  agent: "project.detect_blocked" };
    case "lost":
      return { label: "Quarterly re-engage",     agent: "email.draft_with_citations" };
  }
}


/** Reads user-created linked Foyr projects from localStorage so
 *  the studio tracker reflects them alongside seeded PROJECTS. */
function readLinkedFoyrProjects(): Array<{
  id: string; name: string; city: string; client: string;
  client_email: string; bhk: string; budgetINR: number;
  deadline: string; status: string;
}> {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(env.VITE_LS_USER_PROJECTS);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

export function getStudioLeads(): readonly DerivedLead[] {
  const out: DerivedLead[] = [];
  const linkedProjects = readLinkedFoyrProjects();
  // Linked Foyr projects land on the tracker as their own funnel
  // rows — newest at top, stage derived from the project status.
  for (const lp of linkedProjects) {
    const ageDays = 0;
    const stage: DerivedLead["stage"] = "qualified";
    const tier = tierFromBudget(lp.budgetINR || 0);
    out.push({
      id: `lead-linked-${lp.id}`,
      name: lp.client && lp.client !== "—" ? lp.client : lp.name,
      email: lp.client_email && lp.client_email !== "—" ? lp.client_email : "—",
      city: lp.city && lp.city !== "—" ? lp.city : "—",
      enquiry: `${lp.name} · linked from Foyr Neo${lp.budgetINR > 0 ? ` · ₹${(lp.budgetINR / 100_000).toFixed(2)} L` : ""}`,
      budget_tier: tier,
      stage,
      created: new Date().toISOString().slice(0, 10),
      project_id: lp.id,
      reason: {
        why: `Linked from Foyr Neo · ${tier} tier · cockpit will re-rank once the project's bundle ingests.`,
        cited_nodes: [lp.id],
        source: `linked-foyr:${lp.id}`,
      },
      source_app: "typeform",
      next_action: nextActionFor(stage, ageDays),
      value_inr: lp.budgetINR || tierIndicativeValue(tier),
      age_days: ageDays,
    });
  }
  // Real projects → real funnel rows (Won / Proposal / Lost).
  for (const p of PROJECTS) {
    let stage: DerivedLead["stage"] | null = null;
    let createdOffsetDays = 0;
    if (p.status === "active" || p.status === "delivered") {
      stage = "won";
      // Won deals were signed ~30–90 days ago depending on project size.
      createdOffsetDays = -30 - Math.round((p.budgetINR / 1_000_000) * 4);
    } else if (p.status === "draft") {
      stage = "proposal";
      createdOffsetDays = -7;
    } else if (p.status === "on-hold") {
      stage = "lost";
      createdOffsetDays = -45;
    }
    if (!stage) continue;
    const alertCount = getProjectAlertCount(p.id);
    const ageDays = Math.abs(createdOffsetDays);
    const reasonWhy =
      stage === "won"
        ? `Project status is ${p.status} — moved to Won when the engagement was signed (~${ageDays} days ago). ${alertCount} open alert${alertCount === 1 ? "" : "s"} on the project graph.`
        : stage === "proposal"
          ? `Project status is draft — proposal is out, awaiting client signature. Created ${ageDays} days ago.`
          : `Project status is on-hold — moved to Lost ${ageDays} days ago. Re-engage if budget/timeline changes.`;
    out.push({
      id: `lead-${p.id}`,
      name: p.client,
      email: p.client_email,
      city: p.city,
      enquiry: `${p.bhk} · ${p.name} · ₹${(p.budgetINR / 100_000).toFixed(2)} L`,
      budget_tier: tierFromBudget(p.budgetINR),
      stage,
      created: todayPlus(createdOffsetDays),
      project_id: p.id,
      reason: {
        why: reasonWhy,
        cited_nodes: [p.id],
        source: `project_status:${p.status}`,
      },
      source_app: inferSourceApp({ email: p.client_email, stage, fromProject: true }),
      next_action: nextActionFor(stage, ageDays),
      value_inr: p.budgetINR,
      age_days: ageDays,
    });
  }
  // Prospect rows for NEW / QUALIFIED come from a typed data file —
  // synthesised lazily here from STUDIO_PROSPECTS with today-relative
  // dates so they never look frozen.
  for (const pr of STUDIO_PROSPECTS) {
    const whyProspect =
      pr.stage === "qualified"
        ? `${pr.budget_tier} tier · matches studio's intake profile · ${pr.aged_days} days since first contact — qualified for proposal.`
        : `${pr.budget_tier} tier · ${pr.aged_days} days since enquiry — awaiting qualification call.`;
    // Tier-indicative budget · env-driven, never literal numbers here.
    const tierValue = tierIndicativeValue(pr.budget_tier);
    out.push({
      id: pr.id,
      name: pr.name,
      email: pr.email,
      city: pr.city,
      enquiry: pr.enquiry,
      budget_tier: pr.budget_tier,
      stage: pr.stage,
      created: todayPlus(-pr.aged_days),
      reason: {
        why: whyProspect,
        cited_nodes: [pr.id],
        source: "agent:lead.from_website_chat",
      },
      source_app: inferSourceApp({ email: pr.email, stage: pr.stage, fromProject: false }),
      next_action: nextActionFor(pr.stage, pr.aged_days),
      value_inr: tierValue,
      age_days: pr.aged_days,
    });
  }
  return out;
}

/* Live alert count — derived from the active project's typed graph.
 * Counts:
 *   • Open milestones with at least one SKU whose leadDays exceed
 *     the safe runway (dueIn − VITE_LEAD_TIME_BUFFER_DAYS).
 *   • Stale approvals (ageDays ≥ VITE_APPROVAL_AGE_DAYS).
 *   • Explicit Alert nodes already in the graph (severity warn/red).
 * The number on the project hero now matches what the cockpit actually
 * shows — never a frozen literal from PROJECTS[].alerts. */
export function getProjectAlertCount(projectId: string): number {
  // Project-targeted variant — used by the ProjectTracker dropdown to
  // show a per-row badge derived live from PROJECT_GRAPHS[projectId].
  const g = PROJECT_GRAPHS[projectId];
  if (!g) return 0;
  return countAlertsForGraph(g);
}

export function getActiveAlertCount(): number {
  return countAlertsForGraph(getActiveProjectGraph());
}

function countAlertsForGraph(g: { nodes: readonly GraphNode[] }): number {
  // Single source of truth: count actual Alert-kind nodes in the
  // graph. Derived signals (long-lead milestones, stale approvals)
  // are materialised as Alert nodes at `buildProjectGraph` time —
  // so the badge count, the visible alerts list, and the 2D/3D viz
  // all walk the same nodes. No drift.
  let count = 0;
  for (const a of g.nodes) {
    if (a.kind !== "Alert") continue;
    const sev = str(a, "severity");
    if (sev === "warn" || sev === "red") count += 1;
  }
  return count;
}

export function getActiveProjectMeta(): { id: string; name: string; client: string; client_email: string; city: string } | null {
  const id = getActiveProjectId();
  const p = PROJECTS.find((x) => x.id === id);
  if (!p) return null;
  return { id: p.id, name: p.name, client: p.client, client_email: p.client_email, city: p.city };
}
